import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface IgnoredWordsPanelProps {
  userIgnored: string[];
  onRestore: (word: string) => void;
}

// User-managed ignore list. Each chip has × to restore the word back into
// future analyses. Real BE: `user_ignored_word(user_id, word, created_at)`.
const IgnoredWordsPanel: React.FC<IgnoredWordsPanelProps> = ({
  userIgnored,
  onRestore,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${
          theme.palette.mode === 'dark'
            ? '#303A48'
            : theme.palette.primary.tableBorder
        }`,
        borderRadius: 2,
        overflow: 'hidden',
      })}
    >
      <Box
        component={'button'}
        type={'button'}
        onClick={() => setOpen((p) => !p)}
        sx={(theme) => ({
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          textAlign: 'left',
          width: '100%',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(155,124,255,0.06)'
                : 'rgba(59,44,147,0.03)',
          },
        })}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <BlockOutlinedIcon sx={{ color: 'text.secondary' }} />
          <Typography fontWeight={700}>{'Ігнор-список'}</Typography>
          <Typography variant={'caption'} color={'text.secondary'}>
            {`${userIgnored.length} слів`}
          </Typography>
        </Stack>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={open} timeout={'auto'}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            variant={'caption'}
            color={'text.secondary'}
            sx={{ display: 'block', mb: 1 }}
          >
            {
              'Ці слова не потраплятимуть у "Потребують уваги" в нових списках. Натисни × щоб повернути слово в аналіз.'
            }
          </Typography>
          {userIgnored.length === 0 ? (
            <Typography variant={'body2'} color={'text.secondary'}>
              {'Поки порожньо.'}
            </Typography>
          ) : (
            <Stack direction={'row'} spacing={0.75} flexWrap={'wrap'} useFlexGap>
              {userIgnored.map((w) => (
                <Chip
                  key={w}
                  label={w}
                  onDelete={() => onRestore(w)}
                  sx={{
                    bgcolor: 'primary.purple',
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default IgnoredWordsPanel;
